import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  APIProvider,
  APILoadingStatus,
  useApiLoadingStatus,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { sentry } from 'utils/sentry.config';

export type MapAvailability = {
  status: 'loading' | 'available' | 'unavailable';
  reason?: 'missing-config' | 'auth-failure' | 'script-failed';
};

export type UseMapAvailabilityOptions = {
  // LOADED-but-pre-AUTH crashes marker code; probe gates mounts on a real round-trip.
  requireProbe?: boolean;
};

const MapAvailabilityContext = createContext<MapAvailability | null>(null);

// Module scope: MapProvider mounts per route, but probe + Sentry must persist
// across remounts. Probe is billed at 1 Autocomplete Request per session.
type PlacesHealth = 'unknown' | 'ok' | 'denied';

let placesHealth: PlacesHealth = 'unknown';
let probePromise: Promise<void> | null = null;
let deniedReported = false;
const placesHealthListeners = new Set<() => void>();

const setPlacesHealth = (next: PlacesHealth) => {
  if (placesHealth === next || placesHealth === 'denied') return;
  placesHealth = next;
  if (next === 'denied' && !deniedReported) {
    deniedReported = true;
    sentry.captureException(
      new Error(
        'Google Maps Places denied — check API key permissions or billing',
      ),
    );
  }
  placesHealthListeners.forEach(fn => fn());
};

const PROBE_TIMEOUT_MS = 5000;

const runPlacesProbe = (
  places: google.maps.PlacesLibrary,
): Promise<void> => {
  if (placesHealth !== 'unknown') return Promise.resolve();
  if (probePromise) return probePromise;
  probePromise = new Promise<void>(resolve => {
    let settled = false;
    const finish = (next: PlacesHealth) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      if (next !== 'unknown') setPlacesHealth(next);
      resolve();
    };
    // Fail open on timeout — real user fetches still flip 'denied'.
    const timeout = window.setTimeout(() => finish('unknown'), PROBE_TIMEOUT_MS);
    places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: 'a',
      includedRegionCodes: ['us'],
    })
      .then(() => finish('ok'))
      .catch(() => finish('denied'));
  });
  return probePromise;
};

const DENIAL_PATTERN = /ApiNotActivatedMapError|REQUEST_DENIED|PERMISSION_DENIED/i;

export const reportPlacesFetchFailure = (error: unknown): void => {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (DENIAL_PATTERN.test(message)) setPlacesHealth('denied');
};

const usePlacesHealth = (): PlacesHealth => {
  const [snapshot, setSnapshot] = useState(placesHealth);
  useEffect(() => {
    setSnapshot(placesHealth);
    const listener = () => setSnapshot(placesHealth);
    placesHealthListeners.add(listener);
    return () => {
      placesHealthListeners.delete(listener);
    };
  }, []);
  return snapshot;
};

export const useMapAvailability = (
  options: UseMapAvailabilityOptions = {},
): MapAvailability => {
  const ctx = useContext(MapAvailabilityContext);
  if (ctx === null) {
    throw new Error('useMapAvailability must be used inside <MapProvider>');
  }

  const { requireProbe = false } = options;
  const places = useMapsLibrary('places');
  const health = usePlacesHealth();

  useEffect(() => {
    if (requireProbe && places) runPlacesProbe(places);
  }, [requireProbe, places]);

  if (health === 'denied') {
    return { status: 'unavailable', reason: 'auth-failure' };
  }
  if (ctx.status === 'unavailable') return ctx;
  if (requireProbe && ctx.status === 'available' && health === 'unknown') {
    return { status: 'loading' };
  }
  return ctx;
};

const InnerMapProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const apiStatus = useApiLoadingStatus();
  const reportedRef = useRef(false);

  const availability = useMemo<MapAvailability>(() => {
    if (apiStatus === APILoadingStatus.AUTH_FAILURE) {
      return { status: 'unavailable', reason: 'auth-failure' };
    }
    if (apiStatus === APILoadingStatus.FAILED) {
      return { status: 'unavailable', reason: 'script-failed' };
    }
    if (apiStatus !== APILoadingStatus.LOADED) {
      return { status: 'loading' };
    }
    return { status: 'available' };
  }, [apiStatus]);

  useEffect(() => {
    if (availability.status !== 'unavailable' || reportedRef.current) return;
    reportedRef.current = true;
    sentry.captureException(
      new Error(
        availability.reason === 'auth-failure'
          ? 'Google Maps auth failed (invalid/restricted key or billing disabled)'
          : 'Google Maps script failed to load',
      ),
    );
  }, [availability]);

  return (
    <MapAvailabilityContext.Provider value={availability}>
      {children}
    </MapAvailabilityContext.Provider>
  );
};

type MapProviderProps = {
  children: ReactNode;
};

export const MapProvider: FC<MapProviderProps> = ({ children }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const mapId = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;

  if (!apiKey || !mapId) {
    return (
      <MapAvailabilityContext.Provider
        value={{ status: 'unavailable', reason: 'missing-config' }}
      >
        {children}
      </MapAvailabilityContext.Provider>
    );
  }

  // Stay mounted on auth failure: unmounting mid-init trips `getRootNode of undefined` in marker code.
  return (
    <APIProvider
      apiKey={apiKey}
      libraries={['places', 'marker']}
      region="US"
      language="en-US"
    >
      <InnerMapProvider>{children}</InnerMapProvider>
    </APIProvider>
  );
};
